import React, { useCallback } from 'react';
import styled from 'styled-components';
import { useIntl } from 'react-intl';
import { FileUpload, SdiFile, SdiTrash, TextLink } from '@sdir/sds';
import { VirtualFile } from '../../../types/filegroup';

type FileSelectProps = {
  availableFiles: VirtualFile[];
  selectedFileOption: VirtualFile | null;
  onSelectFile: (file: VirtualFile) => void;
  onDeleteFile?: () => void;
  onNewFilesAdded: (files: File[]) => void;
  deleteAny?: boolean;
  showWhenEmpty?: boolean;
  buttonText?: string;
};

export const FileSelect = ({
  availableFiles,
  buttonText,
  selectedFileOption,
  onSelectFile,
  onNewFilesAdded,
  onDeleteFile,
  deleteAny = false,
  showWhenEmpty,
}: FileSelectProps) => {
  const intl = useIntl();

  const handleChange = useCallback(
    (file: VirtualFile) => {
      onSelectFile(file);
    },
    [onSelectFile]
  );

  return (
    <>
      {(!!availableFiles.length || showWhenEmpty) && (
        <AvailableFilesAndUpload className={showWhenEmpty ? 'vertical' : ''}>
          <AvailableFiles>
            {availableFiles.map((file, index) => {
              if (!file) return <React.Fragment key={`empty-${index}`} />;
              return (
                <FileLinkItem
                  key={`file-${file?.name || index}`}
                  file={file}
                  isSelected={file === selectedFileOption}
                  index={index}
                  handleChange={handleChange}
                  onDelete={onDeleteFile}
                  deleteAny={deleteAny}
                />
              );
            })}
          </AvailableFiles>
          <FileUpload
            allowedTypes=".png,.jpg,.jpeg,.pdf"
            multiple
            buttonLabel={buttonText || intl.formatMessage({ id: 'filegroup.addfiles' })}
            onFilesSelected={onNewFilesAdded}
          />
        </AvailableFilesAndUpload>
      )}
    </>
  );
};

export const FileLinkItem = (props) => {
  const { file, isSelected, index = 0, handleChange, onDelete, deleteAny } = props;
  const fileName = file?.name || '';
  const intl = useIntl();
  return (
    <FileLinkWrapper key={`file_${fileName}_${index}`}>
      <TextLink
        text={fileName}
        strong={isSelected}
        icon={<SdiFile size="s" height="1em" />}
        onClick={() => handleChange(file)}
      />
      {onDelete && (isSelected || deleteAny) && (
        <TextLink
          text={intl.formatMessage({ id: 'filegroup.deletefile' })}
          icon={<SdiTrash size="s" height="1em" />}
          onClick={onDelete}
        />
      )}
    </FileLinkWrapper>
  );
};

const AvailableFilesAndUpload = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  margin-top: 16px;
  gap: 16px;

  &.vertical {
    flex-direction: column;

    section {
      order: 1;
    }

    > * {
      width: 500px;
    }
  }

  section {
    order: 2;
  }

  > * {
    flex: 0 1 auto;
    width: 240px;
  }

  @media screen and (max-width: ${({ theme }) => theme.screen.medium}) {
    gap: 10px;

    &.vertical {
      > * {
        width: 100%;
      }
    }

    > * {
      width: auto;
    }
  }
`;

const AvailableFiles = styled.div`
  display: flex;
  flex-direction: column;
  order: 1;

  .vertical & {
    order: 2;
  }
`;

const FileLinkWrapper = styled.div`
  display: flex;
  margin: 5px 0;

  button {
    &:first-child {
      word-break: break-all;
    }

    &:last-child:not(:first-child) {
      margin-left: 50px;
    }
  }
`;
